import React, { useState } from "react";
import { MagnifyingGlass, LockKey, LockKeyOpen, Infinity as InfinityIcon, HandGrabbing, TextT } from "@phosphor-icons/react";
import { station } from "../lib/api";
import { toast } from "sonner";

/**
 * ATR / prism-lock actions plus the laser-∞ demo.
 *
 * On this TS12 the ATR subsystem is off in the instrument config (all
 * AUT_* return rc=27) so Search / Lock / Unlock will surface a friendly
 * error until the surveyor toggles ATR on inside the instrument itself.
 * The Demo ∞ button uses the MOT (motor-velocity) subsystem which is
 * available on the same firmware — it's how we can trace a lemniscate
 * on the wall with the visible laser without needing the AUT subsystem.
 */

export const AtrPanel = ({ state, disabled }) => {
  const [busy, setBusy] = useState(null);
  const [periodS, setPeriodS] = useState(2.5); // full ∞ per revolution
  const [sizeDeg, setSizeDeg] = useState(4.0); // half-amplitude in deg
  // Laser text tracing — separate small panel that opens on demand.
  const [textOpen, setTextOpen] = useState(false);
  const [traceText, setTraceText] = useState("");
  const [textSpeed, setTextSpeed] = useState(6.0);
  const searching = state?.atr_searching;
  const locked = state?.atr_locked;
  const demoRunning = state?.demo_running;

  const run = async (action) => {
    if (disabled) return;
    setBusy(action);
    try {
      await station.atr(action);
      toast.success(`ATR ${action.toUpperCase()}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "ATR command failed";
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const toggleDemo = async () => {
    if (disabled) return;
    setBusy("demo");
    try {
      const action = demoRunning ? "stop" : "start";
      await station.demo(action, sizeDeg, periodS);
      toast.success(action === "start" ? "Demo — laser tracing ∞" : "Demo stopped");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Demo command failed";
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  // Re-fire the demo with the new speed if the user drags the slider
  // while it's running. Debounced by React state batching — the last
  // slider value that fires within the same render tick wins.
  const restartDemoWithNewSpeed = async () => {
    if (!demoRunning || disabled) return;
    try {
      await station.demo("start", sizeDeg, periodS);
    } catch {
      /* silent — the periodic user press will pick this up */
    }
  };

  const releaseKnobs = async () => {
    if (disabled) return;
    setBusy("release");
    try {
      await station.releaseMotors();
      toast.success("Motors released — manual knobs are live");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Release failed");
    } finally {
      setBusy(null);
    }
  };

  const startTextTrace = async () => {
    if (disabled) return;
    const clean = (traceText || "").trim();
    if (!clean) {
      toast.error("Type something first — e.g. your name");
      return;
    }
    setBusy("text");
    try {
      await station.demoText(clean, sizeDeg, textSpeed, true);
      toast.success(`Tracing "${clean.slice(0, 20)}" on the wall`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Text trace failed");
    } finally {
      setBusy(null);
    }
  };

  const stopTextTrace = async () => {
    if (disabled) return;
    setBusy("text-stop");
    try {
      // The bridge's demo_text uses the same task slot as the ∞ demo,
      // so hitting the ∞ stop action cancels either one.
      await station.demo("stop");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stop failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="panel" data-testid="atr-panel">
      <div className="panel-header">
        <span>PRISM / ATR</span>
        <div className="flex items-center gap-2">
          <button
            data-testid="atr-release-motors-btn"
            onClick={releaseKnobs}
            disabled={disabled || busy !== null}
            className="flex items-center gap-1 rounded-sm border border-[var(--border)] px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-[var(--text-dim)] transition-colors hover:border-[var(--primary)] hover:text-[var(--text)] disabled:opacity-40"
            title="Release Hz/V servos so you can turn the head with the manual drive knobs"
          >
            <HandGrabbing size={11} weight="bold" />
            Release knobs
          </button>
          <div className={`led ${locked ? "led-on" : searching ? "led-warn" : "led-off"}`} />
          <span className="text-[10px] tracking-widest text-[var(--text-dim)]">
            {locked ? "LOCKED" : searching ? "SEARCHING" : "UNLOCKED"}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 p-4">
        <button
          data-testid="atr-search-btn"
          onClick={() => run("search")}
          disabled={disabled || busy !== null}
          className="btn-tac flex flex-col items-center justify-center gap-1"
        >
          <MagnifyingGlass size={22} weight="bold" />
          <span className="text-xs">Search</span>
        </button>
        <button
          data-testid="atr-lock-btn"
          onClick={() => run("lock")}
          disabled={disabled || busy !== null}
          className="btn-tac flex flex-col items-center justify-center gap-1"
          data-variant={locked ? "primary" : undefined}
        >
          <LockKey size={22} weight="bold" />
          <span className="text-xs">Lock</span>
        </button>
        <button
          data-testid="atr-unlock-btn"
          onClick={() => run("unlock")}
          disabled={disabled || busy !== null}
          className="btn-tac flex flex-col items-center justify-center gap-1"
        >
          <LockKeyOpen size={22} weight="bold" />
          <span className="text-xs">Unlock</span>
        </button>
        <button
          data-testid="atr-demo-btn"
          onClick={toggleDemo}
          disabled={disabled || busy !== null}
          className="btn-tac flex flex-col items-center justify-center gap-1"
          data-variant={demoRunning ? "danger" : "measure"}
          title="Laser ∞ demo — aim at a wall then press to trace an infinity symbol"
        >
          <InfinityIcon size={22} weight="bold" />
          <span className="text-xs">{demoRunning ? "Stop" : "Demo ∞"}</span>
        </button>
      </div>

      {/* Demo speed + size sliders — only appear while the trace is
          running so the panel stays clean when idle. */}
      {demoRunning && (
        <div className="grid grid-cols-2 gap-4 border-t border-[var(--border)] px-4 py-3">
          <label className="flex flex-col gap-1 text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
            <div className="flex items-center justify-between">
              <span>Speed</span>
              <span className="font-mono text-[var(--text)]">{periodS.toFixed(1)} s</span>
            </div>
            <input
              data-testid="demo-speed-slider"
              type="range"
              min={0.8}
              max={6.0}
              step={0.1}
              value={periodS}
              onChange={(e) => setPeriodS(parseFloat(e.target.value))}
              onMouseUp={restartDemoWithNewSpeed}
              onTouchEnd={restartDemoWithNewSpeed}
              className="accent-[var(--primary)]"
            />
            <span className="text-[9px] text-[var(--text-mute)]">
              faster ← slider → slower
            </span>
          </label>
          <label className="flex flex-col gap-1 text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
            <div className="flex items-center justify-between">
              <span>Size</span>
              <span className="font-mono text-[var(--text)]">{sizeDeg.toFixed(1)}°</span>
            </div>
            <input
              data-testid="demo-size-slider"
              type="range"
              min={1.0}
              max={15.0}
              step={0.5}
              value={sizeDeg}
              onChange={(e) => setSizeDeg(parseFloat(e.target.value))}
              onMouseUp={restartDemoWithNewSpeed}
              onTouchEnd={restartDemoWithNewSpeed}
              className="accent-[var(--primary)]"
            />
            <span className="text-[9px] text-[var(--text-mute)]">
              width of the ∞ on the wall
            </span>
          </label>
        </div>
      )}
      {/* Laser text tracer — collapsible so the panel stays clean when
          the surveyor isn't showing off. Uses the same demo_running
          state slot on the bridge, so the ∞ demo and text tracer are
          mutually exclusive (starting one stops the other). */}
      <div className="border-t border-[var(--border)]">
        <button
          data-testid="text-trace-toggle-btn"
          onClick={() => setTextOpen((v) => !v)}
          className="flex w-full items-center justify-between px-4 py-2 text-[10px] uppercase tracking-widest text-[var(--text-dim)] transition-colors hover:text-[var(--text)]"
          type="button"
        >
          <span className="flex items-center gap-1.5">
            <TextT size={12} weight="bold" /> Trace text on wall
          </span>
          <span className="text-[9px] text-[var(--text-mute)]">
            {textOpen ? "▼" : "▶"}
          </span>
        </button>
        {textOpen && (
          <div className="space-y-3 border-t border-[var(--border)] px-4 py-3">
            <label className="block text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
              Text (A-Z, 0-9, punctuation — max 40 chars)
              <input
                data-testid="text-trace-input"
                type="text"
                value={traceText}
                onChange={(e) => setTraceText(e.target.value)}
                maxLength={40}
                placeholder="e.g. WILSON"
                className="mt-1 w-full rounded-sm border border-[var(--border)] bg-[var(--surface-2)] px-2 py-1.5 font-mono text-sm uppercase tracking-widest text-[var(--text)] placeholder:text-[var(--text-mute)] focus:border-[var(--primary)] focus:outline-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !demoRunning) startTextTrace();
                }}
              />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex flex-col gap-1 text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
                <div className="flex items-center justify-between">
                  <span>Cap height</span>
                  <span className="font-mono text-[var(--text)]">{sizeDeg.toFixed(1)}°</span>
                </div>
                <input
                  data-testid="text-trace-size-slider"
                  type="range"
                  min={2.0}
                  max={12.0}
                  step={0.5}
                  value={sizeDeg}
                  onChange={(e) => setSizeDeg(parseFloat(e.target.value))}
                  className="accent-[var(--primary)]"
                />
              </label>
              <label className="flex flex-col gap-1 text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
                <div className="flex items-center justify-between">
                  <span>Speed</span>
                  <span className="font-mono text-[var(--text)]">{textSpeed.toFixed(1)}°/s</span>
                </div>
                <input
                  data-testid="text-trace-speed-slider"
                  type="range"
                  min={2.0}
                  max={30.0}
                  step={0.5}
                  value={textSpeed}
                  onChange={(e) => setTextSpeed(parseFloat(e.target.value))}
                  className="accent-[var(--primary)]"
                />
              </label>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="text-trace-start-btn"
                onClick={startTextTrace}
                disabled={disabled || busy !== null || !traceText.trim()}
                className="btn-tac flex-1 !min-h-[36px] text-[11px]"
                data-variant="measure"
              >
                {demoRunning ? "Restart trace" : "Start trace"}
              </button>
              <button
                data-testid="text-trace-stop-btn"
                onClick={stopTextTrace}
                disabled={disabled || !demoRunning}
                className="btn-tac flex-1 !min-h-[36px] text-[11px]"
                data-variant="danger"
              >
                Stop
              </button>
            </div>
            <p className="text-[9px] leading-relaxed text-[var(--text-mute)]">
              Aim the head at your target wall first — the text is drawn
              centred on whatever the laser is currently pointing at. The
              laser loops the string until you press Stop or Release knobs.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
